
import { GlRenderer } from "./glrenderer";
import { loadImage } from "./gl/texture";
import { loadPropAssetsWithRetry } from "./gl/props";
import { HeightField, type HeightFieldMeta } from "./heightfield";
import { ObjectSurface, loadSurfaceModels } from "./objectsurface";
import { decodeDds, type RgbaImage } from "./image";
import { decodeShade } from "./shade";
import { WaterMap } from "./watermap";
import { Renderer } from "./renderer";
import type { SceneRenderer } from "./scene";
import { TileMap } from "./tiles";
import {
  DEFAULT_ENVIRONMENT,
  ENVIRONMENT_INDEX_URL,
  resolveNightEnvironment,
  type ActiveMap,
} from "./mapassets";

export const TERRAIN_ATLAS_FETCH_RETRIES = 1;
export const TERRAIN_ATLAS_RETRY_DELAY_MS = 250;

export async function loadTerrainAtlasWithRetry<T>(
  loadAtlas: () => Promise<T>,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<T> {
  let failure: unknown;
  for (let attempt = 0; attempt <= TERRAIN_ATLAS_FETCH_RETRIES; attempt++) {
    try {
      return await loadAtlas();
    } catch (error) {
      failure = error;
      if (attempt === TERRAIN_ATLAS_FETCH_RETRIES) break;
      await wait(TERRAIN_ATLAS_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

export const TERRAIN_HEIGHT_FETCH_RETRIES = 1;
export const TERRAIN_HEIGHT_RETRY_DELAY_MS = 250;

export async function loadTerrainHeightWithRetry<T>(
  loadHeight: () => Promise<T>,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<T> {
  let failure: unknown;
  for (let attempt = 0; attempt <= TERRAIN_HEIGHT_FETCH_RETRIES; attempt++) {
    try {
      return await loadHeight();
    } catch (error) {
      failure = error;
      if (attempt === TERRAIN_HEIGHT_FETCH_RETRIES) break;
      await wait(TERRAIN_HEIGHT_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

/** The baked normal map controls the terrain's visible relief. Recover one
 * transient Safari decode before preserving the existing flat-shading fallback
 * for a genuinely absent or malformed map. */
export const TERRAIN_NORMAL_FETCH_RETRIES = 1;
export const TERRAIN_NORMAL_RETRY_DELAY_MS = 250;

export async function loadTerrainNormalWithRetry<T>(
  loadNormal: () => Promise<T>,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<T> {
  let failure: unknown;
  for (let attempt = 0; attempt <= TERRAIN_NORMAL_FETCH_RETRIES; attempt++) {
    try {
      return await loadNormal();
    } catch (error) {
      failure = error;
      if (attempt === TERRAIN_NORMAL_FETCH_RETRIES) break;
      await wait(TERRAIN_NORMAL_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

/** The baked shadow coverage is a visible terrain input. Recover one transient
 * Safari decode before preserving the existing full-sun fallback for a truly
 * absent or malformed shadow map. */
export const TERRAIN_SHADOW_FETCH_RETRIES = 1;
export const TERRAIN_SHADOW_RETRY_DELAY_MS = 250;

export async function loadTerrainShadowWithRetry<T>(
  loadShadow: () => Promise<T>,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<T> {
  let failure: unknown;
  for (let attempt = 0; attempt <= TERRAIN_SHADOW_FETCH_RETRIES; attempt++) {
    try {
      return await loadShadow();
    } catch (error) {
      failure = error;
      if (attempt === TERRAIN_SHADOW_FETCH_RETRIES) break;
      await wait(TERRAIN_SHADOW_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

async function loadHeightField(map: ActiveMap): Promise<HeightField> {
  const metaUrl = map.assets.heightMeta;
  const samplesUrl = map.assets.height;
  try {
    return await loadTerrainHeightWithRetry(async () => {
      const [meta, samples] = await Promise.all([
        fetch(metaUrl).then((response) => {
          if (!response.ok) throw new Error(`${metaUrl}: HTTP ${response.status}`);
          return response.json() as Promise<HeightFieldMeta>;
        }),
        fetch(samplesUrl).then(async (response) => {
          if (!response.ok) throw new Error(`${samplesUrl}: HTTP ${response.status}`);
          const bytes = await response.arrayBuffer();
          const view = new DataView(bytes);
          const samples = new Uint16Array(bytes.byteLength / 2);
          for (let i = 0; i < samples.length; i++) samples[i] = view.getUint16(i * 2, true);
          return samples;
        }),
      ]);
      return HeightField.from(samples, meta, map);
    });
  } catch (error) {
    console.warn(`terrain height unavailable (${String(error)}); rendering ground flat`);
    return HeightField.flat(map);
  }
}

export async function createRenderer(
  canvas: HTMLCanvasElement,
  map: ActiveMap,
): Promise<SceneRenderer> {
  // Started immediately rather than awaited here: both renderers need it, and
  // there is no reason for it to wait behind the atlas fetch below.
  const heightFieldPromise = loadHeightField(map);
  // Started here rather than inside the try: it is one small JSON body, it is
  // the same file `resolveMap` already read, and a browser serves the second
  // request out of its cache. Never rejects -- a missing night record means the
  // night flag does not change the light, not that the map cannot be drawn.
  const nightPromise = fetch(ENVIRONMENT_INDEX_URL)
    .then((response) => (response.ok ? response.json() : null))
    .then((index) => resolveNightEnvironment(index))
    .catch(() => null);

  try {
    // The shadow and normal maps, and the buildings/trees, are fetched
    // alongside the required assets but are allowed to fail on their own:
    // flat, unshadowed, undecorated ground is a smaller loss than a world
    // drawn by the fallback renderer, which is what a throw here would cost.
    const propUrls = map.assets.placements && map.assets.meshIndex && map.assets.meshBlob &&
      map.assets.textureIndex && map.assets.textureBase
      ? {
          placementsUrl: map.assets.placements,
          meshIndexUrl: map.assets.meshIndex,
          meshBlobUrl: map.assets.meshBlob,
          textureIndexUrl: map.assets.textureIndex,
          textureBaseUrl: map.assets.textureBase,
        }
      : null;
    const [atlas, tiles, shadow, normal, heightField, props, surfaces, water, shade] = await Promise.all([
      loadTerrainAtlasWithRetry(() => loadImage(map.assets.atlas)),
      TileMap.fetch(map.assets.tiles),
      loadTerrainShadowWithRetry(() => loadImage(map.assets.shadow)).catch((error) => {
        console.warn(`shadow map unavailable (${String(error)}); rendering in full sun`);
        return null;
      }),
      loadTerrainNormalWithRetry(() => loadImage(map.assets.normal)).catch((error) => {
        console.warn(`normal map unavailable (${String(error)}); rendering ground flat`);
        return null;
      }),
      heightFieldPromise,
      (propUrls === null ? Promise.resolve(null) : loadPropAssetsWithRetry(propUrls)).catch((error) => {
        console.warn(`buildings/trees unavailable (${String(error)}); rendering the terrain alone`);
        return null;
      }),
      propUrls === null ? Promise.resolve({}) : loadSurfaceModels(),
      loadWater(map.key),
      (map.assets.shade === undefined ? Promise.resolve(null) : loadImage(map.assets.shade).then(decodeShade))
        .catch((error) => {
          console.warn(`shade map unavailable (${String(error)}); characters are lit in full colour`);
          return null;
        }),
    ]);
    if (props !== null && Object.keys(surfaces).length > 0) {
      heightField.setObjectSurface(new ObjectSurface(surfaces, props.placements.buildings));
    }
    const renderer = new GlRenderer(canvas, atlas, tiles, heightField, shadow, normal, props);
    // Before any frame: the record carries the clear colour as well as the
    // fade, so a renderer drawn once without it paints one frame of the wrong
    // map's sky. `map.environment` is `resolveMap`'s answer for this map's own
    // `setting.txt` row; the night record is the pack's `moonlight04`.
    renderer.setEnvironment(map.environment ?? DEFAULT_ENVIRONMENT, await nightPromise);
    renderer.setWater(water.map, water.frames, performance.now());
    renderer.setShade(shade);
    return renderer;
  } catch (error) {
    console.warn(`WebGL2 terrain unavailable (${String(error)}); falling back to Canvas2D`);
    return new Renderer(canvas, await heightFieldPromise);
  }
}

/** `tools/build_water.py`'s frames: `d:/ymir Work/special/water/01..30.dds`
 *  (`MapOutdoorWater.cpp:8-17`). */
const WATER_FRAMES = 30;

/** A map's baked water and the frames it is drawn with, or nothing: a map with
 *  no water ships no bake, and a failed fetch leaves the lakes undrawn rather
 *  than the map. */
async function loadWater(key: string): Promise<{ map: WaterMap | null; frames: RgbaImage[] }> {
  try {
    const response = await fetch(`water/${key}.bin`);
    if (!response.ok) return { map: null, frames: [] };
    const map = WaterMap.decode(await response.arrayBuffer());
    const frames = await Promise.all(
      Array.from({ length: WATER_FRAMES }, async (_, index) => {
        const url = `water/frames/${String(index + 1).padStart(2, "0")}.dds`;
        const frame = await fetch(url);
        if (!frame.ok) throw new Error(`${url}: HTTP ${frame.status}`);
        const image = decodeDds(await frame.arrayBuffer());
        if (image === null) throw new Error(`${url}: not a DXT1 frame`);
        return image;
      }),
    );
    return { map, frames };
  } catch (error) {
    console.warn(`water unavailable (${String(error)}); lakes are left undrawn`);
    return { map: null, frames: [] };
  }
}
